/**
 * AppLayout Component
 *
 * Persistent shell rendered around authenticated pages.
 * Provides a top header with the app name and language switcher,
 * and a navigation bar (desktop: top; mobile: bottom tab bar).
 * Auth pages (login / register / onboarding) render outside this layout.
 */

import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { LanguageSwitcher } from './LanguageSwitcher';
import type { ReactNode } from 'react';
import './appLayout.css';

interface AppLayoutProps {
  children: ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { t } = useTranslation('common');
  const { isAuthenticated, logout } = useAuthStore();
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate('/login');
  }

  return (
    <div className="app-layout">
      {/* ── Top Header ── */}
      <header className="app-layout-header">
        <div className="app-layout-header-inner">
          <NavLink to="/" className="app-layout-logo">
            {t('appName')}
          </NavLink>

          <div className="app-layout-header-right">
            <LanguageSwitcher />
            {isAuthenticated && (
              <button
                className="app-layout-logout"
                onClick={handleLogout}
                aria-label={t('logout')}
              >
                {t('logout')}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* ── Navigation (only when authenticated) ── */}
      {isAuthenticated && (
        <nav className="app-layout-nav" aria-label="Main navigation">
          <NavLink
            to="/recipes"
            className={({ isActive }) =>
              `app-layout-nav-link${isActive ? ' app-layout-nav-link--active' : ''}`
            }
          >
            <span className="app-layout-nav-icon" aria-hidden="true">🍽️</span>
            <span className="app-layout-nav-label">{t('viewRecipes')}</span>
          </NavLink>

          <NavLink
            to="/meal-plan"
            className={({ isActive }) =>
              `app-layout-nav-link${isActive ? ' app-layout-nav-link--active' : ''}`
            }
          >
            <span className="app-layout-nav-icon" aria-hidden="true">📅</span>
            <span className="app-layout-nav-label">{t('mealPlan')}</span>
          </NavLink>

          <NavLink
            to="/shopping-list"
            className={({ isActive }) =>
              `app-layout-nav-link${isActive ? ' app-layout-nav-link--active' : ''}`
            }
          >
            <span className="app-layout-nav-icon" aria-hidden="true">🛒</span>
            <span className="app-layout-nav-label">{t('shoppingList')}</span>
          </NavLink>

          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `app-layout-nav-link${isActive ? ' app-layout-nav-link--active' : ''}`
            }
          >
            <span className="app-layout-nav-icon" aria-hidden="true">⚙️</span>
            <span className="app-layout-nav-label">{t('settings')}</span>
          </NavLink>
        </nav>
      )}

      {/* ── Page Content ── */}
      <main className="app-layout-main" id="main-content">
        {children}
      </main>
    </div>
  );
}
